/**
 * useCameraCapture — USB webcam media-stream manager with zero-retention disposal.
 *
 * Story 70-1D (E-70 OCR pipeline). Used by <CameraCaptureDialog />.
 *
 * Architecture:
 * - getUserMedia with Logitech C920 deviceId preference (label match)
 * - Optional MediaTrackConstraints.advanced.focusMode=manual when supported
 * - Fallback to default device if C920 not enumerated
 * - Zero-retention per ADR-104 D-4 + GDPR DPIA Light §5:
 *   tracks stopped, blob URLs revoked, canvas cleared, refs nulled on dispose.
 *
 * NO persistence: images never touch localStorage, IndexedDB, or the network
 * from this hook — caller receives Blobs and is responsible for downstream
 * OCR dispatch + revocation.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

export type CameraPermission = 'pending' | 'requesting' | 'granted' | 'denied' | 'no-device';

export interface UseCameraCaptureResult {
  permission: CameraPermission;
  error: string | null;
  stream: MediaStream | null;
  videoRef: React.RefObject<HTMLVideoElement | null>;
  canvasRef: React.RefObject<HTMLCanvasElement | null>;
  focusModeSupported: boolean;
  start: () => Promise<void>;
  stop: () => void;
  captureFrame: () => Promise<Blob | null>;
}

const LOGITECH_C920_HINT = /c920|logitech/i;

async function selectDeviceId(): Promise<string | undefined> {
  if (!navigator.mediaDevices?.enumerateDevices) return undefined;
  try {
    const devices = await navigator.mediaDevices.enumerateDevices();
    const cams = devices.filter(d => d.kind === 'videoinput');
    const c920 = cams.find(d => LOGITECH_C920_HINT.test(d.label));
    return c920?.deviceId;
  } catch {
    return undefined;
  }
}

export function useCameraCapture(): UseCameraCaptureResult {
  const [permission, setPermission] = useState<CameraPermission>('pending');
  const [error, setError] = useState<string | null>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [focusModeSupported, setFocusModeSupported] = useState(false);

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const stop = useCallback((): void => {
    const s = streamRef.current;
    if (s) {
      s.getTracks().forEach(t => {
        try {
          t.stop();
        } catch {
          // ignore
        }
      });
    }
    streamRef.current = null;
    setStream(null);

    // Detach video element so the browser releases buffers.
    if (videoRef.current) {
      try {
        videoRef.current.srcObject = null;
      } catch {
        // ignore
      }
    }

    // Wipe canvas (zero-retention): clearRect + reset dimensions to 0.
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
      }
      canvas.width = 0;
      canvas.height = 0;
    }
  }, []);

  const start = useCallback(async (): Promise<void> => {
    setError(null);
    setPermission('requesting');

    if (!navigator.mediaDevices?.getUserMedia) {
      setPermission('no-device');
      setError('getUserMedia not supported');
      return;
    }

    try {
      const preferredDeviceId = await selectDeviceId();
      const videoConstraints = preferredDeviceId
        ? {
            deviceId: { exact: preferredDeviceId },
            width: { ideal: 1920 },
            height: { ideal: 1080 },
          }
        : { width: { ideal: 1920 }, height: { ideal: 1080 } };

      const media = await navigator.mediaDevices.getUserMedia({
        video: videoConstraints,
        audio: false,
      });

      streamRef.current = media;
      setStream(media);

      // Attempt manual focus — only if the driver exposes focusMode capability.
      const [track] = media.getVideoTracks();
      if (track && typeof track.getCapabilities === 'function') {
        const caps = track.getCapabilities() as { focusMode?: string[] };
        if (caps.focusMode && caps.focusMode.includes('manual')) {
          setFocusModeSupported(true);
          try {
            await track.applyConstraints({
              advanced: [{ focusMode: 'manual' } as unknown as Record<string, unknown>],
            });
          } catch {
            // Non-fatal: keep auto-focus.
          }
        }
      }

      if (videoRef.current) {
        videoRef.current.srcObject = media;
        try {
          await videoRef.current.play();
        } catch {
          // Autoplay policy may suppress — user interaction gesture has already
          // been granted via the permission prompt, so retry silently.
        }
      }

      setPermission('granted');
    } catch (err) {
      const name = (err as DOMException)?.name ?? '';
      if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
        setPermission('denied');
        setError('permission-denied');
      } else if (name === 'NotFoundError' || name === 'DevicesNotFoundError') {
        setPermission('no-device');
        setError('device-not-found');
      } else {
        setPermission('denied');
        setError('device-generic');
      }
    }
  }, []);

  const captureFrame = useCallback(async (): Promise<Blob | null> => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return null;

    const w = video.videoWidth || 1280;
    const h = video.videoHeight || 720;
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(video, 0, 0, w, h);

    return new Promise<Blob | null>(resolve => {
      canvas.toBlob(blob => resolve(blob), 'image/jpeg', 0.92);
    });
  }, []);

  // Unmount cleanup — defense in depth so Dialog consumers can't leak.
  useEffect(() => {
    return () => {
      stop();
    };
  }, [stop]);

  return {
    permission,
    error,
    stream,
    videoRef,
    canvasRef,
    focusModeSupported,
    start,
    stop,
    captureFrame,
  };
}
